import { node } from "~/util/dom";
import { CONFIG } from "~/config";
import { setCoordinates, setMapset } from "~/state/map/state";
import { store } from "./state/store";

interface IPromoData {
	fromDate: Date | Array<Date>;
	toDate: Date | Array<Date>;
	className?: string;
	img: {
		[key: string]: string;
	}
	link?: string;
	analyticsClick?: string;
	analyticsImpress?: () => void;
	onclick?: (event: MouseEvent) => void;
	targetBlank?: boolean;
	langs?: Array<string>;
}

const mapyComBlogURL = (lang: string) => {
	switch (lang) {
		case "de":
			return "https://blog.seznam.cz/de/2025/03/der-weg-von-mapy-cz-zu-mapy-com-beginnt/";
		case "en":
			return "https://blog.seznam.cz/en/2025/03/the-journey-from-mapy-cz-to-mapy-com-begins/";
		case "it":
			return "https://blog.seznam.cz/it/2025/03/il-viaggio-da-mapy-cz-a-mapy-com-sta-iniziando/";
		case "pl":
			return "https://blog.seznam.cz/pl/2025/03/droga-od-mapy-cz-do-mapy-com-wlasnie-sie-rozpoczyna/";
		case "sk":
			return "https://blog.seznam.cz/sk/2025/03/cesta-od-mapy-cz-k-mapy-com-prave-zacina/";
		default:
			return "https://blog.seznam.cz/2023/03/cesta-od-mapy-cz-k-mapy-com-dve-pismena-velke-moznosti/";
	}
};

export default class PromoHP {
	_dom: {
		container: HTMLDivElement;
	};

	_curLang: string;

	_data: Array<IPromoData>;

	_selItem: IPromoData;

	constructor() {
		this._dom = {
			container: node("div", { id: "promohp", className: "noprint" }),
		};
		this._curLang = CONFIG.defaultLang;
		this._data = [{
			fromDate: new Date(2024, 2, 4),
			toDate: new Date(2024, 3, 26, 23, 59, 59),
			img: {
				"1x": `/img/promo/smetana.png`,
				"2x": `/img/promo/smetana@2x.png`,
			},
			className: "smetana-promo",
			link: "https://mapy.cz/s/dofonadoze",
			analyticsClick: "banner_smetana_24",
			langs: ["cs"],
		}, {
			fromDate: new Date(2025, 0, 21),
			toDate: new Date(2025, 1, 28, 23, 59, 59),
			img: {
				"1x": `/img/promo/bezky_${this._curLang}.jpg`,
				"2x": `/img/promo/bezky_${this._curLang}@2x.jpg`,
			},
			className: "bezky-promo",
			link: "/zimni?x=15.2222580&y=50.7973890&z=12",
			onclick: event => {
				event.preventDefault();

				store.dispatch(setMapset({ mapset: "zimni" }));
				store.dispatch(setCoordinates({
					longitude: 15.2222580,
					latitude: 50.7973890,
					zoom: 12,
				}));
				Mapy.getComponent("history").save();
			},
			analyticsClick: "banner_bezky_25",
			langs: ["cs", "en", "pl", "de", "sk"],
		}, {
			fromDate: new Date(2025, 2, 24, 10, 0, 0),
			toDate: new Date(2025, 3, 20, 23, 59, 59),
			img: {
				"1x": `/img/promo/mapycom_${this._curLang}.jpg`,
				"2x": `/img/promo/mapycom_${this._curLang}@2x.jpg`,
			},
			className: "mapycom-promo",
			link: mapyComBlogURL(this._curLang),
			targetBlank: true,
			analyticsClick: "banner_mapycom_25",
			langs: ["cs", "de", "en", "it", "pl", "sk"],
		}];
		this._selItem = null;

		this._build();
	}

	impress() {
		// musi byt timeout pro prvni zobrazeni
		setTimeout(() => {
			if (this._selItem?.analyticsImpress && Mapy.getComponent("search").isActive()) {
				this._selItem.analyticsImpress();
			}
		}, 0);
	}

	getContainer() {
		return this._dom.container;
	}

	_build() {
		let cur = Date.now();
		let findItem: IPromoData = null;

		if (Mapy.CUS.userweb.actualDate) {
			try {
				const parse = Mapy.CUS.userweb.actualDate.split(".");

				cur = new Date(parseInt(parse[2]), parseInt(parse[1])-1, parseInt(parse[0])).getTime();
			} catch (error) {
				// error
			}
		}

		// najdeme aktualni
		this._data.every(item => {
			let notFind = true;

			if (!Array.isArray(item.fromDate)) {
				item.fromDate = [item.fromDate];
			}

			if (!Array.isArray(item.toDate)) {
				item.toDate = [item.toDate];
			}

			if (item.fromDate.length !== item.toDate.length) { // pokud se nam neshoduje delka pole preskocime, spatne zadana data
				return notFind;
			}

			for (let index = 0; index < item.fromDate.length; index++) {
				const fromdate = item.fromDate[index].getTime();
				const todate = item.toDate[index].getTime();

				if (cur >= fromdate && cur <= todate && (!item.langs || item.langs.indexOf(this._curLang) !== -1)) {
					findItem = item;
					notFind = false;
					break;
				}
			}

			return notFind;
		});

		this._selItem = findItem;

		if (findItem) {
			this._createItem(this._selItem);
		} else {
			this._dom.container.classList.add("hide");
		}
	}

	_createItem(data: IPromoData) {
		const link = node("a", { className: "link-item" });

		if (data.className) {
			link.classList.add(data.className);
		}

		const img = new Image();

		link.appendChild(img);
		// posleme pred klikem
		if (data.analyticsClick) {
			link.addEventListener("pointerdown", () => {
				Mapy.getComponent("logger").log(data.analyticsClick);
			});
		}

		link.href = data.link || "#";
		img.srcset = Object.keys(data.img).map(key => `${data.img[key]} ${key}`).join(", ");
		img.src = data.img["1x"];
		img.crossOrigin = "anonymous";

		if (typeof data.onclick === "function") {
			link.addEventListener("click", data.onclick);
		}

		if (data.targetBlank) {
			link.setAttribute("target", "_blank");
		}

		this._dom.container.appendChild(link);

		return link;
	}
}
